import appConfig from '../config/appConfig';
import {
    BaseUser,
    IDEmployee,
    IDGroup,
    IDContract,
    IDNameRole,
    IDContractType,
    EmailEmployee,
    PhoneEmployee,
} from '../core/apiTypes';
import { getUser } from '../localStorageAPI';
import { MAX_USERS_FULLNAME_LENGTH } from './constants';
import { isWebView } from './webview';

//= ===================================//
//= ============== USER ===============//
//= ===================================//

// const singletonUser: IDEmployee = getUser() ?? null;

export const getConnectedUser = (): IDEmployee => {
    // if (!singletonUser || _.isEmpty(singletonUser)) {
    //     singletonUser = getUser();
    // }
    // return singletonUser;
    return getUser();
};

export const getFullname = (user: IDEmployee | BaseUser): string => {
    if (!user) {
        return null;
    }
    return `${user.firstname} ${user.lastname}`;
};

export const getFullnameMaxed = (
    user: IDEmployee | BaseUser,
    maxLength: number = MAX_USERS_FULLNAME_LENGTH,
): string => {
    const fullname = getFullname(user);
    if (fullname && fullname.length > maxLength) {
        return fullname.slice(0, maxLength) + '...';
    }
    return fullname;
};

export const getPrimaryPhone = (user: IDEmployee = getConnectedUser()): PhoneEmployee => {
    return user?.phones?.find((p) => p.primary) || user?.phones?.find((p) => !p.pro);
};

export const getPrimaryEmail = (user: IDEmployee = getConnectedUser()): EmailEmployee => {
    return user?.emails?.find((e) => e.primary) || user?.emails?.find((p) => !p.pro);
};

export const getProEmail = (user: IDEmployee = getConnectedUser()): string => {
    return user?.emails?.find((e) => e.pro)?.email;
};

// Impersonate
export const isUserImpersonating = (user: IDEmployee = getConnectedUser()): boolean => {
    return !!user?.puppeteer;
};

// Groups
export const getGroups = (user: IDEmployee = getConnectedUser()): IDGroup[] => {
    return user?.groups || [];
};

export const getContracts = (user: IDEmployee = getConnectedUser()): IDContract[] => {
    return user?.contracts || [];
};

export const isUserAdmin = (user: IDEmployee = getConnectedUser()): boolean => {
    // Disable admin on WebView
    if (isWebView()) {
        return false;
    }

    return (
        user?.contracts?.some((el) => el.plan.type === IDContractType.CORPORTATE) ||
        user?.groups?.some(
            (el) =>
                el.roles.some((elem) => elem.role === IDNameRole.PRINCIPAL) &&
                el.contract.plan.type === IDContractType.CORPORTATE,
        )
    );
};

export const isUserGroupAdmin = (groupId: string, user: IDEmployee = getConnectedUser()): boolean => {
    return user?.groups?.some((g) => g.uuid === groupId && g.roles.some((elem) => elem.role === IDNameRole.PRINCIPAL));
};

export const isUserContractAdmin = (contractId: string, user: IDEmployee = getConnectedUser()): boolean => {
    return user?.contracts?.some((el) => el.uuid === contractId);
};

export const isUserContractAdminOrPrincipalForGroup = (
    groupId: string,
    user: IDEmployee = getConnectedUser(),
): boolean => {
    const { contracts, groups } = user;

    // Check if the user has the "PRINCIPAL" role in any group
    const hasPrincipalRole = groups?.some((group) => {
        return group.uuid === groupId && group.roles.find((role) => role.role === IDNameRole.PRINCIPAL);
    });

    if (hasPrincipalRole) {
        return true;
    }

    // Check if the group is linked to a contract in the user's contracts array
    const groupLinkedToContract = groups?.find((group) => group.uuid === groupId);

    if (groupLinkedToContract) {
        const userHasContract = contracts.some((contract) => contract.uuid === groupLinkedToContract.contract.uuid);

        if (userHasContract) {
            return true;
        }
        return false;
    }
    return false;
};

export const getDefaultGroup = (user: IDEmployee = getConnectedUser()): IDGroup => {
    if (appConfig.isDev) {
        return user?.groups.find((g) => g.contract.plan.type === IDContractType.CGU);
    }
    return (
        user?.groups?.find((g) => g.contract.plan.type === IDContractType.CORPORTATE) ||
        user?.groups?.find((g) => g.contract.plan.type === IDContractType.CGU)
    );
};

export const isContractPerso = (groupId: string, user: IDEmployee = getConnectedUser()): boolean => {
    return user?.groups?.some((el) => el.uuid === groupId && el.contract.plan.type === IDContractType.CGU);
};

export const getDefaultContractID = (user: IDEmployee = getConnectedUser()): string => {
    return (
        user?.contracts?.find((el) => el.plan.type === IDContractType.CORPORTATE)?.uuid ||
        user?.groups?.find((el) => el.contract.plan.type === IDContractType.CORPORTATE)?.contract?.uuid
    );
};

export const getB2CContractID = (user: IDEmployee = getConnectedUser()): string => {
    return user?.contracts?.find((el) => el.plan.type === IDContractType.CGU)?.uuid;
};

export const getB2CGroupID = (user: IDEmployee = getConnectedUser()): string => {
    return user?.groups?.find((el) => el.contract.plan.type === IDContractType.CGU)?.uuid;
};

export const getB2BGroupID = (user: IDEmployee = getConnectedUser()): string => {
    return user?.groups?.find((el) => el.contract.plan.type === IDContractType.CORPORTATE)?.uuid;
};

export const getGroup = (groupId: string, user: IDEmployee = getConnectedUser()): IDGroup => {
    return user?.groups?.find((el) => el.uuid === groupId);
};

export const isUserFullB2C = (user: IDEmployee = getConnectedUser()): boolean => {
    return !isB2B(user);
};

export const isB2B = (user: IDEmployee = getConnectedUser()): boolean => {
    return !!getDefaultContractID(user);
};
